import type { IFileLink } from "../../../file";
import type { ExplicitType } from "../../../utils";
import { isOfType } from "../../../utils";
import type {
  IAttributeCondition,
  IAttributeParameter,
  IAttributeProperty,
  IAttributePropertyAndConditions,
  IRealValue,
} from "../attributes";
import type { ILinkByUid, Uids } from "../utils";

import type {
  IMaterialTemplate,
  IMeasurementTemplate,
  IObjectTemplate,
  IProcessTemplate,
} from "./templates";

export interface IIngredientQuantities {
  mass_fraction?: IRealValue;
  volume_fraction?: IRealValue;
  number_fraction?: IRealValue;
  absolute_quantity?: IRealValue;
}

/**
 * An representation of an object from Material History data
 */
export interface ISpecObjectBase {
  uids: Uids;
  name: string;
  tags: string[];
  notes?: string;
  file_links: IFileLink[];
  audit_info?: {
    created_by: string; // user id of who created the object
    created_at: number; // timestamp of when it was created
    updated_by?: string; // user id of who last updated the object
    updated_at?: number; // timestamp of whne it was last updated
  };
  // data object can sometimes have dataset field, this is an id of which dataset this data object was created in
  dataset?: string;
  persisted?: boolean;
  // indicates that something is being done to the object in the UI
  processing?: boolean;

  conditions?: IAttributeCondition[];
  parameters?: IAttributeParameter[];
  properties?: IAttributePropertyAndConditions[] | IAttributeProperty[];
}

export interface IIngredientSpec
  extends ISpecObjectBase, IIngredientQuantities {
  type: "ingredient_spec";
  conditions?: never;
  parameters?: never;
  properties?: never;

  labels: string[];
  process: ILinkByUid;
  material: ILinkByUid;
}

interface ISpecObjectWithTemplateBase<
  TemplateShape extends IObjectTemplate,
> extends ISpecObjectBase {
  // specs can optionally have a template and sometimes its a link by id / sometimes its the full object
  template?: ILinkByUid | TemplateShape;
}

export interface IMaterialSpec extends ISpecObjectWithTemplateBase<IMaterialTemplate> {
  type: "material_spec";
  conditions?: never;
  parameters?: never;
  properties: IAttributePropertyAndConditions[];

  process: ILinkByUid | IProcessSpec;
}

export interface IMeasurementSpec extends ISpecObjectWithTemplateBase<IMeasurementTemplate> {
  type: "measurement_spec";
  properties?: never;
}

export interface IProcessSpec extends ISpecObjectWithTemplateBase<IProcessTemplate> {
  type: "process_spec";
  properties?: never;

  ingredients?: ILinkByUid[];
  // output material comes from the backend, not a required field for when we try to create the object for the first time
  output_material?: ILinkByUid;
}

// only these 3 types have an associate template field, ingredient doesn't
export type ISpecObjectWithTemplate =
  | IMaterialSpec
  | IMeasurementSpec
  | IProcessSpec;
export type SpecObjectWithTemplateType = ISpecObjectWithTemplate["type"];

export type ISpecObject = IIngredientSpec | ISpecObjectWithTemplate;
export type SpecObjectType = ISpecObject["type"];

export const isIngredientSpec = isOfType<IIngredientSpec>("ingredient_spec");
export const isMaterialSpec = isOfType<IMaterialSpec>("material_spec");
export const isMeasurementSpec = isOfType<IMeasurementSpec>("measurement_spec");
export const isProcessSpec = isOfType<IProcessSpec>("process_spec");

export const isSpecObjectWithTemplate = (
  obj?: ExplicitType<string>,
): obj is ISpecObjectWithTemplate =>
  isMaterialSpec(obj) || isMeasurementSpec(obj) || isProcessSpec(obj);

export const isSpecObject = (obj?: ExplicitType<string>): obj is ISpecObject =>
  isSpecObjectWithTemplate(obj) || isIngredientSpec(obj);
